import ChatInterface from "@/components/feature/Chat/ChatInterface"
import { createEmptyChat } from "@/components/feature/Chat/utils/chatUtils"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { CHAT_PANEL_HEADER_HEIGHT } from "@/constants/UiConstants"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useChatContext } from "@/contexts/ChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useLayoutStore } from "@/store/layoutStore"
import {
  ArrowLeftIcon,
  CheckIcon,
  HistoryIcon,
  PanelRightIcon,
  SquarePenIcon,
  XIcon,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"
import { ChatThreadList } from "./ChatThreadList"

export interface ChatPanelProps {
  className?: string
  /** Spread onto the header row; the floating window attaches drag listeners. */
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}

export function ChatPanel({ className, dragHandleProps }: ChatPanelProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  const { chatPanelMode, setChatPanelMode, closeChatPanel } = useLayoutStore()
  const { setChat } = useChatContext()
  const agentChat = useAgentChatContext()
  const [view, setView] = useState<"chat" | "history">("chat")

  const isHistoryView = chatHistoryEnabled && view === "history"

  const handleNewChat = () => {
    setChat(createEmptyChat())
    agentChat.resetForNewConversation()
    setView("chat")
  }

  const handleFullPage = () => {
    closeChatPanel()
    navigate("/chat")
  }

  return (
    <div className={cn("flex h-full flex-col bg-card", className)}>
      <div
        className="flex shrink-0 items-center gap-1 border-b border-border px-3"
        style={{ height: CHAT_PANEL_HEADER_HEIGHT }}
        {...dragHandleProps}
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleNewChat}
          aria-label={translate("AI_CHAT/NEW_CONVERSATION")}
          title={translate("AI_CHAT/NEW_CONVERSATION")}
        >
          <SquarePenIcon className="h-4 w-4" />
        </Button>
        <span className="flex-1 truncate text-sm font-medium">
          {isHistoryView
            ? translate("AI_CHAT/HISTORY/PAGE_TITLE")
            : translate("AI_CHAT/ASSISTANT")}
        </span>
        {chatHistoryEnabled ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setView(isHistoryView ? "chat" : "history")}
            aria-label={
              isHistoryView
                ? translate("AI_CHAT/ASSISTANT")
                : translate("AI_CHAT/SHEET/TOGGLE_THREADS")
            }
            title={
              isHistoryView
                ? translate("AI_CHAT/ASSISTANT")
                : translate("AI_CHAT/SHEET/TOGGLE_THREADS")
            }
          >
            {isHistoryView ? (
              <ArrowLeftIcon className="h-4 w-4" />
            ) : (
              <HistoryIcon className="h-4 w-4" />
            )}
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={translate("AI_CHAT/PANEL/LAYOUT_MENU")}
              title={translate("AI_CHAT/PANEL/LAYOUT_MENU")}
            >
              <PanelRightIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setChatPanelMode("docked")}>
              <CheckIcon
                className={cn(
                  "mr-2 h-4 w-4",
                  chatPanelMode !== "docked" && "invisible",
                )}
              />
              {translate("AI_CHAT/PANEL/LAYOUT_SIDEBAR")}
            </DropdownMenuItem>
            {!isMobile ? (
              <DropdownMenuItem onSelect={() => setChatPanelMode("floating")}>
                <CheckIcon
                  className={cn(
                    "mr-2 h-4 w-4",
                    chatPanelMode !== "floating" && "invisible",
                  )}
                />
                {translate("AI_CHAT/PANEL/LAYOUT_FLOATING")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={handleFullPage}>
              <CheckIcon className="invisible mr-2 h-4 w-4" />
              {translate("AI_CHAT/PANEL/LAYOUT_FULL_PAGE")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={closeChatPanel}
          aria-label={translate("AI_CHAT/PANEL/CLOSE")}
          title={translate("AI_CHAT/PANEL/CLOSE")}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {isHistoryView ? (
          <ChatThreadList onSelectConversation={() => setView("chat")} />
        ) : (
          <ChatInterface syncUrl={false} compact />
        )}
      </div>
    </div>
  )
}
